import logger from '../utils/logger';

// Types d'événements supportés par le service
export type EventType = 
  | 'MARKER_CATEGORY_CHANGED'
  | 'MARKERS_UPDATED'
  | 'CATEGORY_DELETED'
  | 'REFRESH_MAP_MARKERS'
  | 'OPEN_MARKER_MODAL'
  | 'ANIMATE_MARKER'
  | 'SIDEBAR_TOGGLE'
  | 'OPEN_MARKER_POPUP'
  | 'NOTIFICATIONS_CATEGORY_UPDATED'
  | 'NOTIFICATION_CREATED'
  | 'START_ZONE_DRAWING'
  | 'DRAWING_COORDINATES_UPDATE'
  | 'ZONE_DRAWING_COMPLETE'
  | 'CANCEL_ZONE_DRAWING'
  | 'RELOAD_ZONES'
  | 'ZONE_VISIBILITY_CHANGED'
  | 'OPEN_ZONE_POPUP';

// Interface pour les abonnements aux événements
interface EventSubscription {
  type: EventType;
  callback: (data: any) => void;
}

/**
 * Service de gestion des événements pour la communication entre composants
 */
class EventsService {
  private subscribers: EventSubscription[] = [];
  private subscriberIds: Map<string, number> = new Map(); // Pour suivre les composants qui s'abonnent
  
  /**
   * S'abonner à un événement
   * @param type Type d'événement
   * @param callback Fonction à appeler lors de l'événement
   * @param componentId Identifiant optionnel du composant qui s'abonne
   * @returns Fonction pour se désabonner
   */
  subscribe(type: EventType, callback: (data: any) => void, componentId?: string): () => void {
    // Si un componentId est fourni, vérifier s'il est déjà abonné à cet événement
    if (componentId) {
      const key = `${type}-${componentId}`;
      const existingIndex = this.subscriberIds.get(key);
      
      // Si ce composant est déjà abonné, remplacer son callback
      if (existingIndex !== undefined) {
        this.subscribers[existingIndex] = { type, callback };
        logger.debug(`Mise à jour de l'abonnement existant pour ${componentId} à l'événement ${type}`);
        return () => this.unsubscribe(type, componentId);
      }
      
      // Sinon, enregistrer ce nouvel abonnement
      this.subscriberIds.set(key, this.subscribers.length);
    }
    
    const subscription: EventSubscription = { type, callback };
    this.subscribers.push(subscription);
    
    logger.debug(`Nouvel abonnement à l'événement ${type}`, {
      totalSubscribers: this.subscribers.filter(s => s.type === type).length,
      componentId
    });
    
    // Retourner une fonction pour se désabonner
    return () => {
      if (componentId) {
        this.unsubscribe(type, componentId);
      } else {
        this.subscribers = this.subscribers.filter(s => s !== subscription);
        logger.debug(`Désabonnement de l'événement ${type}`, {
          totalSubscribers: this.subscribers.filter(s => s.type === type).length
        });
      }
    };
  }
  
  /**
   * Se désabonner d'un événement
   * @param type Type d'événement
   * @param componentId Identifiant du composant
   */
  private unsubscribe(type: EventType, componentId: string): void {
    const key = `${type}-${componentId}`;
    const index = this.subscriberIds.get(key);
    
    if (index !== undefined) {
      // Supprimer l'abonnement
      this.subscribers.splice(index, 1);
      this.subscriberIds.delete(key);
      
      // Mettre à jour les indices des autres abonnements
      this.subscriberIds.forEach((value, key) => {
        if (value > index) {
          this.subscriberIds.set(key, value - 1);
        }
      });
      
      logger.debug(`Désabonnement de l'événement ${type} pour ${componentId}`, {
        totalSubscribers: this.subscribers.filter(s => s.type === type).length
      });
    }
  }
  
  /**
   * Émettre un événement
   * @param type Type d'événement
   * @param data Données associées à l'événement
   */
  emit(type: EventType, data?: any): void {
    logger.debug(`Émission de l'événement ${type}`, { data });
    
    const relevantSubscribers = this.subscribers.filter(s => s.type === type);
    
    if (relevantSubscribers.length === 0) {
      logger.debug(`Aucun abonné pour l'événement ${type}`);
      return;
    }
    
    relevantSubscribers.forEach(subscriber => {
      try {
        subscriber.callback(data);
      } catch (error) {
        logger.error(`Erreur lors de l'exécution du callback pour l'événement ${type}:`, error);
      }
    });
    
    logger.debug(`Événement ${type} traité par ${relevantSubscribers.length} abonnés`);
  }
}

// Exporter une instance unique du service
export const eventsService = new EventsService();

